import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import Logo from './Logo';
import LanguageSwitcher from './ui/LanguageSwitcher';
import { useT } from '../i18n/I18nContext';
import { trackEvent } from '../analytics';

// All nav links target sections on the landing page or a separate route.
// Type 'hash'  → anchor on `/` (`/#how-it-works`)
// Type 'route' → another page (`/example`)
type NavTarget =
  | { kind: 'hash';  hash: string }
  | { kind: 'route'; to:   string; newTab?: boolean };

export default function Header() {
  const headerRef = useRef<HTMLElement>(null);
  const innerRef  = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Smooth-scroll to an in-page anchor. If we are not on `/`, navigate there
  // first — the ScrollToHash listener in App.tsx will pick up the hash and
  // scroll once the landing has rendered.
  const goToHash = (hash: string) => (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      const el = document.querySelector(hash);
      if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Update the URL hash without jumping.
      window.history.replaceState(null, '', hash);
      setMenuOpen(false);
    } else {
      e.preventDefault();
      navigate('/' + hash);
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.9, ease: 'power2.out', delay: 0.2 }
      );
    });

    const onScroll = () => {
      if (!innerRef.current) return;
      const scrolled = window.scrollY > 30;
      innerRef.current.style.background = scrolled
        ? 'rgba(255,255,255,0.22)'
        : 'rgba(255,255,255,0.14)';
      innerRef.current.style.boxShadow = scrolled
        ? '0 14px 44px rgba(80,45,120,0.26), inset 0 1px 0 rgba(255,255,255,0.45)'
        : '0 10px 36px rgba(80,45,120,0.18), inset 0 1px 0 rgba(255,255,255,0.4)';
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const t = useT();
  const navItems: { label: string; target: NavTarget }[] = [
    { label: t.header.nav.howItWorks, target: { kind: 'hash',  hash: '#how-it-works' } },
    { label: t.header.nav.example,    target: { kind: 'route', to:   '/example'      } },
    { label: t.header.nav.faq,        target: { kind: 'hash',  hash: '#faq'          } },
  ];

  // Render either a Link (route nav) or an anchor (hash w/ smooth-scroll handler).
  const renderNavItem = (
    label: string,
    target: NavTarget,
    style: React.CSSProperties,
    onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void,
    onMouseLeave?: (e: React.MouseEvent<HTMLAnchorElement>) => void,
    onClick?: () => void,
  ) => {
    const handleClickWrapper = (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.();
      if (target.kind === 'hash') goToHash(target.hash)(e);
    };
    if (target.kind === 'route') {
      return (
        <Link
          to={target.to}
          style={style}
          onClick={() => { trackEvent('nav_click', { label, destination: target.to }); onClick?.(); setMenuOpen(false); }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {label}
        </Link>
      );
    }
    return (
      <a
        href={'/' + target.hash}
        style={style}
        onClick={(e) => { trackEvent('nav_click', { label, destination: target.hash }); handleClickWrapper(e); }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {label}
      </a>
    );
  };

  return (
    <header
      ref={headerRef}
      style={{
        position: 'fixed',
        top: 18,
        left: 'clamp(16px, 2vw, 28px)',
        right: 'clamp(16px, 2vw, 28px)',
        zIndex: 1000,
        opacity: 0,
      }}
    >
      <div
        ref={innerRef}
        style={{
          height: 68,
          display: 'flex',
          alignItems: 'center',
          padding: '0 clamp(18px, 2.5vw, 32px)',
          background: 'rgba(255,255,255,0.14)',
          backdropFilter: 'blur(24px) saturate(150%)',
          WebkitBackdropFilter: 'blur(24px) saturate(150%)',
          border: '1px solid rgba(255,255,255,0.28)',
          borderRadius: 999,
          boxShadow: '0 10px 36px rgba(80,45,120,0.18), inset 0 1px 0 rgba(255,255,255,0.4)',
          transition: 'background 0.35s ease, box-shadow 0.35s ease',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle inner gradient reflection */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 50%)',
          pointerEvents: 'none',
          borderRadius: 999,
        }} />

        {/* Logo → home */}
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', gap: 10,
          textDecoration: 'none', position: 'relative', zIndex: 1,
        }}>
          <Logo size={32} />
          <span style={{
            fontWeight: 700,
            fontSize: 20,
            color: '#FFFFFF',
            letterSpacing: '-0.02em',
            textShadow: '0 1px 2px rgba(80,45,120,0.18)',
          }}>
            SkinBloom
          </span>
        </Link>

        {/* Language switcher — pinned next to the logo so its position is
            stable when nav labels change length across languages. */}
        <div className="header-lang" style={{ marginLeft: 14, position: 'relative', zIndex: 1, flexShrink: 0 }}>
          <LanguageSwitcher />
        </div>

        {/* Desktop nav */}
        <nav style={{
          display: 'flex',
          gap: 4,
          marginLeft: 'auto',
          marginRight: 16,
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }} className="desktop-nav">
          {navItems.map(item => (
            <React.Fragment key={item.label}>
              {renderNavItem(
                item.label,
                item.target,
                {
                  padding: '8px 14px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.78)',
                  textDecoration: 'none',
                  borderRadius: 100,
                  transition: 'color 0.2s ease, background 0.2s ease',
                },
                e => {
                  (e.target as HTMLAnchorElement).style.color = 'rgba(255,255,255,1)';
                  (e.target as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.1)';
                },
                e => {
                  (e.target as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.78)';
                  (e.target as HTMLAnchorElement).style.background = 'transparent';
                },
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* CTA → /get-started */}
        <Link
          to="/get-started"
          onClick={() => trackEvent('cta_clicked', { location: 'header', label: 'primary' })}
          className="header-cta-primary"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '10px 20px',
            fontSize: 13,
            fontWeight: 700,
            color: 'white',
            textDecoration: 'none',
            background: 'linear-gradient(135deg, #F36BAF 0%, #8B6CFF 100%)',
            borderRadius: 999,
            boxShadow: '0 6px 22px rgba(243,107,175,0.45)',
            transition: 'transform 0.22s ease, box-shadow 0.22s ease',
            position: 'relative',
            zIndex: 1,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(243,107,175,0.6)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 22px rgba(243,107,175,0.45)';
          }}
        >
          {t.header.cta}
        </Link>

        {/* Mobile burger — pushed to the right edge when nav+CTA are hidden */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            marginLeft: 'auto',
            padding: 6,
            position: 'relative',
            zIndex: 1,
          }}
          className="burger-btn"
          aria-label="Меню"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            {menuOpen ? (
              <>
                <line x1="4" y1="4" x2="18" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <line x1="18" y1="4" x2="4" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </>
            ) : (
              <>
                <line x1="3" y1="6"  x2="19" y2="6"  stroke="white" strokeWidth="2" strokeLinecap="round" />
                <line x1="3" y1="11" x2="19" y2="11" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <line x1="3" y1="16" x2="19" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{
          marginTop: 8,
          background: 'rgba(255,255,255,0.14)',
          backdropFilter: 'blur(28px) saturate(140%)',
          WebkitBackdropFilter: 'blur(28px) saturate(140%)',
          border: '1px solid rgba(255,255,255,0.22)',
          borderRadius: 24,
          padding: '8px 16px 20px',
          boxShadow: '0 10px 40px rgba(80,45,120,0.22)',
        }}>
          {navItems.map(item => (
            <React.Fragment key={item.label}>
              {renderNavItem(
                item.label,
                item.target,
                {
                  display: 'block',
                  padding: '12px 0',
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  textDecoration: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.12)',
                },
                undefined,
                undefined,
                () => setMenuOpen(false),
              )}
            </React.Fragment>
          ))}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <LanguageSwitcher />
          </div>
          <Link
            to="/get-started"
            onClick={() => { trackEvent('cta_clicked', { location: 'header_mobile', label: 'primary' }); setMenuOpen(false); }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              marginTop: 12,
              padding: '12px 22px',
              width: '100%',
              fontSize: 14,
              fontWeight: 700,
              color: 'white',
              textDecoration: 'none',
              background: 'linear-gradient(135deg, #F36BAF 0%, #8B6CFF 100%)',
              borderRadius: 999,
              boxShadow: '0 6px 22px rgba(243,107,175,0.45)',
            }}
          >
            {t.header.cta}
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .burger-btn  { display: block !important; }
          header > div > .header-cta-primary { display: none !important; }
          .header-lang { display: none !important; }
        }
      `}</style>
    </header>
  );
}
